import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import PickEntry from "./PickEntry";
import Leaderboard from "./Leaderboard";
import Fixtures from "./Fixtures";
import Standings from "./Standings";
import WhatIf from "./WhatIf";
import teams from "./teams";
import Flag from "./Flag";

function ContestDetail({ user, contest, onLeave }) {
  const [page, setPage] = useState("leaderboard");
  const [myEntries, setMyEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteStatus, setInviteStatus] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [contestMessage, setContestMessage] = useState(contest.message || "");
  const [editingMessage, setEditingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState(contest.message || "");
  const [savingMessage, setSavingMessage] = useState(false);

  useEffect(() => { fetchMyEntries(); }, []);

  const fetchMyEntries = async () => {
    const q = query(collection(db, "entries"), where("contestId", "==", contest.id), where("userId", "==", user.uid));
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setMyEntries(data);
    if (data.length > 0) setExpandedEntries({ [data[0].id]: true });
  };

  const saveMessage = async () => {
    setSavingMessage(true);
    try {
      await updateDoc(doc(db, "contests", contest.id), { message: messageDraft.trim() });
      setContestMessage(messageDraft.trim());
      setEditingMessage(false);
    } catch (e) { console.error("Failed to save message:", e); }
    setSavingMessage(false);
  };

  const isOwner = user.uid === contest.adminUid;
  const isOpenPool = contest.openPool !== false;
  const allowMultipleEntries = contest.allowMultipleEntries !== false;
  const canInvite = isOwner || isOpenPool;
  const canSeeCode = isOwner || isOpenPool;
  const canAddEntry = allowMultipleEntries || myEntries.length === 0;

  const copyJoinCode = () => {
    navigator.clipboard.writeText(contest.joinCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const sendInvite = async () => {
    setInviteStatus("");
    if (!inviteEmail.trim()) { setInviteStatus("Please enter an email address."); return; }
    setInviteSending(true);
    try {
      const fn = getFunctions();
      await httpsCallable(fn, "sendInviteEmail")({ toEmail: inviteEmail.trim(), contestName: contest.name, joinCode: contest.joinCode, fromName: user.displayName || "A friend" });
      setInviteStatus("Invite sent!");
      setInviteEmail("");
      setTimeout(() => { setInviteStatus(""); setShowInvite(false); }, 2500);
    } catch (err) { setInviteStatus("Failed to send. Please try again."); }
    setInviteSending(false);
  };

  const deleteEntry = async (entryId, entryName) => {
    if (!window.confirm("Delete entry \"" + entryName + "\"? This cannot be undone.")) return;
    setDeletingId(entryId);
    try {
      await deleteDoc(doc(db, "entries", entryId));
      await fetchMyEntries();
      if (editingEntry && editingEntry.id === entryId) { setEditingEntry(null); setShowForm(false); }
    } catch (e) { console.error("Delete failed:", e); }
    setDeletingId(null);
  };

  const downloadCSV = async () => {
    const fn = getFunctions();
    let allEntries = [];
    try {
      const result = await httpsCallable(fn, "getContestEmails")({ contestId: contest.id });
      allEntries = result.data.entries || [];
    } catch {
      const snap = await getDocs(query(collection(db, "entries"), where("contestId", "==", contest.id)));
      allEntries = snap.docs.map((d) => d.data());
    }
    allEntries.sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));

    const getTeamName = (localId) => {
      const team = teams.find((t) => t.id === localId);
      return team ? team.name : localId || "";
    };

    const headers = ["Rank","Entry Name","Player","Email","Score","T1","T2","T3","T4","T5","T6","T7","T8","T9","T10"];
    const rows = allEntries.map((e, i) => [
      i + 1,
      e.entryName || "",
      e.userName || "",
      e.userEmail || "",
      e.totalScore || 0,
      ...([1,2,3,4,5,6,7,8,9,10].map((t) => getTeamName(e.picks ? e.picks["tier" + t] : null)))
    ]);

    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = contest.name.replace(/[^a-z0-9]/gi, "_") + "_entries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const getTeam = (teamId) => teams.find((t) => t.id === teamId);

  const tabs = [
    { id: "leaderboard", label: "🏆 Leaderboard" },
    { id: "pick", label: "⚽ My Entries" },
    { id: "fixtures", label: "📅 Fixtures" },
    { id: "standings", label: "📊 Standings" },
    { id: "whatif", label: "🔮 What If" },
  ];

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: 20 }}>
        <button onClick={onLeave} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>← Back to Pools</button>

        <div style={{ background: "linear-gradient(135deg, #112240, #1a2d4a)", border: "1px solid rgba(255,215,0,0.15)", borderRadius: 16, padding: "16px 20px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: 1 }}>{contest.name}</div>
                {isOwner && <span className="badge badge-yellow">ADMIN</span>}
                {contest.picksLocked && <span className="badge badge-red">🔒 LOCKED</span>}
                {!allowMultipleEntries && <span className="badge badge-gray">1 ENTRY</span>}
                {!isOpenPool && <span className="badge badge-gray">CLOSED</span>}
              </div>
              {canSeeCode && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Join code:</span>
                  <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: "#FFD700", letterSpacing: 4 }}>{contest.joinCode}</span>
                  <button onClick={copyJoinCode} className="btn btn-ghost btn-sm" style={{ padding: "3px 10px" }}>{copied ? "✓ Copied!" : "Copy"}</button>
                </div>
              )}
              {!canSeeCode && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 6, fontStyle: "italic" }}>This is a closed pool — contact the organizer for an invite.</div>}
            </div>
            <button onClick={() => { setEditingEntry(null); setShowForm(true); setPage("pick"); window.scrollTo({top:0,behavior:"smooth"}); }} className="btn btn-success btn-sm">+ New Entry</button>
            {canInvite && <button onClick={() => setShowInvite(!showInvite)} className="btn btn-outline btn-sm">📧 Invite</button>}
            {isOwner && <button onClick={downloadCSV} className="btn btn-ghost btn-sm">⬇ Export</button>}
          </div>

          {showInvite && canInvite && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>Invite someone to <strong style={{ color: "white" }}>{contest.name}</strong></div>

              {/* Direct link */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>SHARE LINK</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input readOnly value={"https://gannigames.com?join=" + contest.joinCode}
                    className="input" style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.6)" }}
                    onFocus={(e) => e.target.select()} />
                  <button onClick={() => { navigator.clipboard.writeText("https://gannigames.com?join=" + contest.joinCode); setInviteStatus("Link copied!"); setTimeout(() => setInviteStatus(""), 2000); }}
                    className="btn btn-ghost btn-sm">📋 Copy</button>
                </div>
              </div>

              {/* Email invite */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>SEND EMAIL INVITE</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendInvite()} placeholder="Email address" className="input" style={{ flex: 1 }} />
                  <button onClick={sendInvite} disabled={inviteSending} className="btn btn-primary">{inviteSending ? "..." : "Send"}</button>
                </div>
              </div>

              {inviteStatus && <p className={inviteStatus === "Invite sent!" || inviteStatus === "Link copied!" ? "success-text" : "error-text"} style={{ marginTop: 8 }}>{inviteStatus}</p>}
            </div>
          )}
        </div>

        {(contestMessage || isOwner) && !showForm && (
          <div style={{ background: "#1a2d4a", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 12, padding: "14px 18px" }}>
            {!editingMessage ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,215,0,0.5)", letterSpacing: 1, marginBottom: 6 }}>📌 FROM THE ORGANIZER</div>
                  {contestMessage ? (
                    <p style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{contestMessage}</p>
                  ) : (
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", fontStyle: "italic", margin: 0 }}>No message yet. Click Edit to add one.</p>
                  )}
                </div>
                {isOwner && <button onClick={() => { setMessageDraft(contestMessage); setEditingMessage(true); }} className="btn btn-ghost btn-sm">Edit</button>}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,215,0,0.5)", letterSpacing: 1, marginBottom: 8 }}>📌 EDIT ORGANIZER MESSAGE</div>
                <textarea value={messageDraft} onChange={(e) => setMessageDraft(e.target.value)} placeholder="Share rules, buy-in info, Venmo details..." className="input" style={{ minHeight: 100, resize: "vertical", lineHeight: 1.5, marginBottom: 10 }} maxLength={500} />
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", textAlign: "right", marginBottom: 10 }}>{messageDraft.length}/500</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveMessage} disabled={savingMessage} className="btn btn-primary btn-sm">{savingMessage ? "Saving..." : "Save Message"}</button>
                  <button onClick={() => setEditingMessage(false)} className="btn btn-ghost btn-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nav-tabs">
        {tabs.map((t) => (
          <button key={t.id} className={"nav-tab" + (page === t.id ? " active" : "")} onClick={() => { setPage(t.id); setShowForm(false); }}>{t.label}</button>
        ))}
      </div>

      {page === "leaderboard" && <Leaderboard contest={contest} user={user} onEditEntry={(entry) => { setEditingEntry(entry); setShowForm(true); setPage("pick"); window.scrollTo({top:0,behavior:"smooth"}); }} />}
      {page === "fixtures" && <Fixtures contest={contest} user={user} />}
      {page === "standings" && <Standings contest={contest} user={user} />}
      {page === "whatif" && <WhatIf contest={contest} user={user} />}

      {page === "pick" && (
        <div>
          {myEntries.length > 0 && !showForm && (
            <div style={{ marginBottom: 20 }}>
              {myEntries.map((e) => {
                const isExpanded = !!expandedEntries[e.id];
                return (
                  <div key={e.id} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, marginBottom: 12, overflow: "hidden", background: "#1a2d4a" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer", borderLeft: "4px solid #FFD700" }}
                      onClick={() => setExpandedEntries(prev => ({ ...prev, [e.id]: !prev[e.id] }))}>
                      <div>
                        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: 1 }}>{e.entryName}</div>
                        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#FFD700" }}>{e.totalScore || 0} PTS</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {!contest.picksLocked && (
                          <>
                            <button onClick={(ev) => { ev.stopPropagation(); setEditingEntry(e); setShowForm(true); window.scrollTo({top:0,behavior:"smooth"}); }} className="btn btn-ghost btn-sm">Edit</button>
                            <button onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.id, e.entryName); }} disabled={deletingId === e.id} className="btn btn-danger btn-sm">{deletingId === e.id ? "..." : "Delete"}</button>
                          </>
                        )}
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>{isExpanded ? "▲" : "▼"}</span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,215,0,0.6)", letterSpacing: 2, marginBottom: 10 }}>YOUR PICKS</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 6 }}>
                          {[1,2,3,4,5,6,7,8,9,10].map((tier) => {
                            const team = getTeam(e.picks ? e.picks["tier" + tier] : null);
                            return (
                              <div key={tier} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "6px 10px", display: "flex", alignItems: "center", gap: 7 }}>
                                <span style={{ fontSize: 10, color: "rgba(255,215,0,0.6)", fontWeight: 700, minWidth: 18 }}>T{tier}</span>
                                {team ? <><Flag cc={team.cc} size={16} /><span style={{ fontSize: 12, fontWeight: 600 }}>{team.name}</span></> : <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 12 }}>—</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {myEntries.length === 0 && !showForm && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.4)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <p>You have no entries yet in this pool.</p>
            </div>
          )}

          {!contest.picksLocked && !showForm && canAddEntry && (
            <button onClick={() => { setEditingEntry(null); setShowForm(true); window.scrollTo({top:0,behavior:"smooth"}); }} className="btn btn-primary btn-lg" style={{ borderRadius: 10 }}>+ New Entry</button>
          )}

          {!contest.picksLocked && !showForm && !canAddEntry && myEntries.length > 0 && (
            <div style={{ background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.2)", borderRadius: 10, padding: "12px 16px", color: "rgba(255,215,0,0.8)", fontSize: 14 }}>
              ℹ️ This pool only allows one entry per person.
            </div>
          )}

          {contest.picksLocked && !showForm && (
            <div style={{ background: "rgba(229,57,53,0.1)", border: "1px solid rgba(229,57,53,0.3)", borderRadius: 10, padding: "12px 16px", color: "#E53935", fontSize: 14 }}>
              🔒 Picks are locked for this pool.
            </div>
          )}

          {showForm && !contest.picksLocked && (
            <div>
              <button onClick={() => { setShowForm(false); setEditingEntry(null); }} className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }}>← Cancel</button>
              <PickEntry user={user} contest={contest} existingEntry={editingEntry} onSaved={() => { fetchMyEntries(); setShowForm(false); setEditingEntry(null); }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContestDetail;
